import { Component, EventEmitter, Input, Output, OnInit, OnChanges, ChangeDetectorRef, TemplateRef } from '@angular/core';
import { ILabel } from '../ILabel.model';
import { MultiListBoxItem } from '../cpq-multi-list-box-with-selection/multi-list-box-item.model';
import { ActivatedRoute } from '@angular/router';
import { SelectType } from '../cpq-select/cpq-select.component';
import { PermissionType } from '../../users-module/permission-groups-data-table/permission-group.model';
import { AddEditPermissionGroupService } from '../../users-module/add-edit-permission-group/add-edit-permission-group.service';

@Component({
    selector: 'cpq-multi-list-box',
    templateUrl: './cpq-multi-list-box.component.html',
    styleUrls: ['./cpq-multi-list-box.component.scss'],
    providers: [AddEditPermissionGroupService],
    standalone: false
})

export class CpqMultiListBoxComponent implements OnChanges {
  @Input('labelAvailable') labelAvailable: ILabel;
  @Input('labelHelp') labelHelp?: ILabel;
  @Input('labelLink') labelLink?: ILabel;
  @Input('items') items: Array<MultiListBoxItem>;
  @Input('isMultilevelList') isMultilevelList = true;
  @Input('isMultiSelect') isMultiSelect = false;
  @Input('showSearch') showSearch = true;
  @Input('notAsyncUsers') notAsyncUsers = false;
  @Input('itemTemplate') itemTemplate: TemplateRef<any>;

  @Output('onClick') onClick = new EventEmitter<MultiListBoxItem>();
  @Output('linkAction') linkAction?: EventEmitter<any> = new EventEmitter<any>();

  public searchKeywordAvaible: string;
  public basicFilteredItems: Array<MultiListBoxItem>;
  public multiLevelFilteredItems: Array<MultiListBoxItem>;
  public PermissionType = PermissionType;
  public SelectType = SelectType;
  public hasSearchResults = true;
  public itemHeight: number = 29;
  public itemsContainerMaxHeight = 261;
  public lastSelectedIndex: number = 1;
  public isShiftKeyUsedForPreviousSelection: boolean = false;
  public isCtrlKeyUsedForPreviousSelection: boolean = false;

  constructor(public route: ActivatedRoute,
    public userPermissionGroupService: AddEditPermissionGroupService) { }

  ngOnChanges() {
    document.getSelection().removeAllRanges();
    if (this.items && this.items.length > 0) {
      this.initItemLists();
    }
  }

  public searchAvailable() {    
      if (this.isMultilevelList) {
        this.searchMultiLevelList();
      } else {
        this.searchBasicList();
      }
  }

  public clearAttributeSearchInput() {
    this.searchKeywordAvaible = "";
    this.searchAvailable();
  }

  public collapseExpand(group: MultiListBoxItem): void {
    const item = this.items.find(i => i.id === group.id);
    item.isCollapsed = !group.isCollapsed;
    group.isCollapsed = !group.isCollapsed;
  }

  public select(selectedItem: MultiListBoxItem, event: MouseEvent) {    
    if (this.isMultilevelList) {
      this.selectInMultiLevelList(selectedItem);
    } else {
      this.selectInBasicList(selectedItem, event);
    }
    this.selectInSourceItems(selectedItem);
    this.onClick.emit(selectedItem);
  }

  public onLinkClick(event) {
    this.linkAction.emit([event]);
  }

  private initItemLists() {
    if (this.isMultilevelList) {
      this.initMultiLevelList();
    } else {
      this.initBasicList();
    }
  }

  private initMultiLevelList() {
    this.multiLevelFilteredItems = new Array<MultiListBoxItem>();
    this.items.forEach(item => {
      const newItem = this.createNewItem(item);
      newItem.isVisible = true;
      newItem.isCollapsed = newItem.children.findIndex(i => i.isSelected) === -1;
      this.multiLevelFilteredItems.push(newItem);
    });
  }

  private initBasicList() {
    this.basicFilteredItems = new Array<MultiListBoxItem>();
    this.items.forEach(item => {
      if (item.children && item.children.length) {
        item.children.forEach(child => {
          const newChild = this.createNewItem(child);
          newChild.isVisible = true;
          this.basicFilteredItems.push(newChild);
        });
      } else {
        const newItem = this.createNewItem(item);
        newItem.isVisible = true;
        this.basicFilteredItems.push(newItem);
      }
    });
  }

  private createNewItem(item: MultiListBoxItem): MultiListBoxItem {
    const newItem = new MultiListBoxItem();
    newItem.id = item.id;
    newItem.additionalInfo = item.additionalInfo;
    if (item.children) {
      newItem.children = [...item.children];
    }
    newItem.isAsync = item.isAsync;
    newItem.isCollapsed = item.isCollapsed;
    newItem.isVisible = item.isVisible;
    newItem.isSelected = item.isSelected;
    newItem.name = item.name;
    newItem.parentId = item.parentId;
    newItem.visibleAll = item.visibleAll;
    return newItem;
  }

  private selectInMultiLevelList(selectedItem: MultiListBoxItem): void {
    this.multiLevelFilteredItems.forEach(group => {
      group.children.forEach(child => {
        if (this.isMultiSelect) {
          if (child.id === selectedItem.id) {
            child.isSelected = !child.isSelected;
          }
        } else {
          if (child.id === selectedItem.id) {
            child.isSelected = !child.isSelected;
          } else {
            child.isSelected = false;
          }
        }
      });
    });
    this.multiLevelFilteredItems = [...this.multiLevelFilteredItems];
  }

  private selectInBasicList(selectItem: MultiListBoxItem, event: MouseEvent): void {
    document.getSelection().removeAllRanges();
    this.basicFilteredItems.forEach(item => {
      if (this.isMultiSelect) {
        if (event.shiftKey) {
          this.selectRange(selectItem);
        }
        else if (event.ctrlKey) {
          this.selectSingleItemByHoldingCtrl(selectItem);
        }
        else {
          if (item.id === selectItem.id) {
            this.selectSingleItem(selectItem);
          }
        }
      } else {
        if (item.id === selectItem.id) {
          item.isSelected = !item.isSelected;
        } else {
          item.isSelected = false;
        }
      }
    });
    this.basicFilteredItems = [...this.basicFilteredItems];
  }

  private selectSingleItem(selectedItem: MultiListBoxItem) {
    if(this.isShiftKeyUsedForPreviousSelection || this.isCtrlKeyUsedForPreviousSelection) {
        this.deselectAll();
    }

    selectedItem.isSelected = !selectedItem.isSelected;
    this.lastSelectedIndex = this.basicFilteredItems.indexOf(selectedItem);
  }

  private deselectAll() {
    this.basicFilteredItems.filter(item => item.isSelected).forEach(filteredItem => {
        filteredItem.isSelected = false;
    });

    this.isShiftKeyUsedForPreviousSelection = false;
    this.isCtrlKeyUsedForPreviousSelection = false;
  }

  private selectSingleItemByHoldingCtrl(selectedItem: MultiListBoxItem) {
    this.isCtrlKeyUsedForPreviousSelection = true;

    selectedItem.isSelected = !selectedItem.isSelected;
    this.lastSelectedIndex = this.basicFilteredItems.indexOf(selectedItem);
  }

  private selectRange(selectedItem: MultiListBoxItem) {
    this.isShiftKeyUsedForPreviousSelection = true;

    if(this.lastSelectedIndex < this.basicFilteredItems.indexOf(selectedItem)){
        this.basicFilteredItems.filter(item => this.basicFilteredItems.indexOf(item) >= this.lastSelectedIndex && this.basicFilteredItems.indexOf(item) <= this.basicFilteredItems.indexOf(selectedItem)).forEach(filteredItem => {
            filteredItem.isSelected = true;
        });
    }
    else {
        this.basicFilteredItems.filter(item => this.basicFilteredItems.indexOf(item) >= this.basicFilteredItems.indexOf(selectedItem)  && this.basicFilteredItems.indexOf(item) <= this.lastSelectedIndex).forEach(filteredItem => {
            filteredItem.isSelected = true;
        });
    }
  }

  private selectInSourceItems(selectedItem: MultiListBoxItem) {
    this.items.forEach(item => {
      if (item.children && item.children.length) {
        item.children.forEach(child => {
          if (this.isMultiSelect) {
            if (child.id === selectedItem.id) {
              child.isSelected = selectedItem.isSelected;
            }
          } else {
            if (child.id === selectedItem.id) {
              child.isSelected = selectedItem.isSelected;
            } else {
              child.isSelected = false;
            }
          }
        });
      } else {
        if (this.isMultiSelect) {
          if (item.id === selectedItem.id) {
            item.isSelected = selectedItem.isSelected;        
          }
        } else {
          if (item.id === selectedItem.id) {
            item.isSelected = selectedItem.isSelected;
          } else {
            item.isSelected = false;
          }
        }
      }
    });
  }

  private searchMultiLevelList() {
    if (this.searchKeywordAvaible !== '') {
      this.items.forEach(group => {
        group.children.forEach(child => {
          if (child.name.toLowerCase().indexOf(this.searchKeywordAvaible.toLowerCase()) !== -1) {
            child.isVisible = true;
          } else {
            child.isVisible = false;
          }
        });
        const isVisible = group.children.filter(c => c.isVisible).length > 0;
        group.isVisible = isVisible;
        group.isCollapsed = !isVisible;
      });
      this.multiLevelFilteredItems = new Array<MultiListBoxItem>();
      this.items.forEach(group => {
        if (group.isVisible) {
          const newGroup = this.createNewItem(group);
          newGroup.children = group.children.filter(c => c.isVisible);
          this.multiLevelFilteredItems.push(newGroup);
        }
      });
    } else {
      this.initMultiLevelList();
    }
  }

  private searchBasicList() {
    if (this.searchKeywordAvaible !== '') {
      this.items.forEach(group => {
        if (group.children && group.children.length) {
          group.children.forEach(child => {
            if (child.name.toLowerCase().indexOf(this.searchKeywordAvaible.toLowerCase()) !== -1) {
              child.isVisible = true;
            } else {
              child.isVisible = false;
            }
          });
          const isVisible = group.children.filter(c => c.isVisible).length > 0;
          group.isVisible = isVisible;
          group.isCollapsed = !isVisible;
        } else {
          if (group.name.toLowerCase().indexOf(this.searchKeywordAvaible.toLowerCase()) !== -1) {
            group.isVisible = true;
          } else {
            group.isVisible = false;
          }
        }        
      });
      this.basicFilteredItems = new Array<MultiListBoxItem>();
      this.items.forEach(group => {
        if (group.isVisible) {
          const newGroup = this.createNewItem(group);
          if (group.children && group.children.length) {            
            newGroup.children = group.children.filter(c => c.isVisible);
            this.basicFilteredItems.push(...newGroup.children);
          } else {
            this.basicFilteredItems.push(newGroup);
          }          
        }
      });
    } else {
      this.initBasicList();
    }
  }
}